/**
 * NOTE on redux/payment:
 *  - Real in-app purchase (react-native-iap) is out of scope for web per
 *    direction at the start of this migration. This screen now calls the
 *    real web `payment.checkout()` action (POST /payment/checkout/), which
 *    already exists and already follows the dispatchAsAsync(resolve)
 *    convention — no IAP product/SKU concepts apply here.
 *  - `premiumCheck`/`setPremiumCheck` (mobile nav-flow flag) and
 *    `payment.getPaymentInfo()` (doesn't exist) are dropped; `completePurchase`
 *    just navigates to Home.
 *  - `paymentInfo`/pricing: since there's no getPaymentInfo fetch, the price
 *    shown is a placeholder constant (PREMIUM_ANNUAL_PRICE) — wire this up to
 *    a real price source (e.g. a value returned from checkout, or a
 *    dedicated pricing endpoint) once one exists.
 */
import React, { useState } from 'react';

import CSSModule from 'react-css-modules';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Spinner } from 'components';
import { actions } from 'coupon-common/src/modules/payment';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import { RouteNames } from '../../../constants';
import CostCircle from './CostCircle';
import msg from './messages';
import styles from './PaymentForm.module.scss';

const PREMIUM_ANNUAL_PRICE = '12.00';

const PaymentForm = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [transactionError, setTransactionError] = useState(null);

  const { hasFreePlan, fetching } = useSelector(store => ({
    hasFreePlan: store.customerProfile.data.has_free_plan,
    fetching: store.payment.loading?.checkout,
  }));

  const alreadyPremium = hasFreePlan === false;

  const completePurchase = () => {
    navigate(RouteNames.HOME);
  };

  const handlePurchase = async () => {
    setTransactionError(null);
    setLoading(true);

    const result = await dispatchAsAsync(actions.checkout({ plan: 'premium_annual' }), dispatch);

    setLoading(false);

    if (!result || result?.error) {
      setTransactionError(
        (result && result.error) || 'Something went wrong completing your purchase. Please contact support if you were charged.',
      );
      return;
    }

    completePurchase();
  };

  const isDisabled = loading || fetching;

  return (
    <div styleName="page">
      <div styleName="card">
        <CostCircle price={PREMIUM_ANNUAL_PRICE} text={intl.formatMessage(msg.plusTax)} />

        {alreadyPremium ? (
          <div styleName="premium-notice">
            <p styleName="premium-title">You&apos;re already a premium member ✅</p>
            <p styleName="premium-subtitle">No payment is required.</p>
          </div>
        ) : (
          <>
            <button type="button" styleName="pay-btn" onClick={handlePurchase} disabled={isDisabled}>
              {loading || fetching ? <Spinner show size="sm" /> : intl.formatMessage(msg.payBtn)}
            </button>

            {transactionError && (
              <div styleName="error-box">
                <p styleName="error-text">⚠️ {transactionError}</p>
              </div>
            )}
          </>
        )}

        <p styleName="notes">{intl.formatMessage(msg.premiumDetails)}</p>
      </div>
    </div>
  );
};

export default CSSModule(PaymentForm, styles, { allowMultiple: true });
